import {
  h,
  defineComponent,
  mergeProps,
  useId,
  provide,
  inject,
  ref,
  onMounted,
  onUpdated,
  watch,
  nextTick
} from 'vue'
import { element, slotContent } from './primitives'
// BootstrapVue uses loose value equality for option and selection models.
const equal = (a, b) => {
  if (a === b) return true
  if (a == null || b == null) return false
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime()
  if (typeof a === 'object' && typeof b === 'object') {
    const keys = Object.keys(a)
    return (
      keys.length === Object.keys(b).length &&
      keys.every((key) => Object.hasOwn(b, key) && equal(a[key], b[key]))
    )
  }
  return typeof a !== 'object' && typeof b !== 'object' && String(a) === String(b)
}
const model = { modelValue: { default: undefined }, value: { default: undefined } }
const current = (p) => (p.modelValue === undefined ? p.value : p.modelValue)
const inputProps = {
  ...model,
  size: String,
  state: { default: null },
  plaintext: Boolean,
  trim: Boolean,
  number: Boolean,
  lazy: Boolean
}
const events = ['update:modelValue', 'input', 'change']
function publish(emit, value, event = 'input') {
  emit('update:modelValue', value)
  emit(event, value)
}
function control(tag) {
  return defineComponent({
    inheritAttrs: false,
    props: inputProps,
    emits: events,
    setup(p, { attrs, emit, expose }) {
      let el
      expose({ focus: () => el?.focus(), blur: () => el?.blur() })
      return () =>
        h(
          tag,
          mergeProps(attrs, {
            ref: (node) => (el = node),
            class: [
              p.plaintext ? 'form-control-plaintext' : 'form-control',
              p.size && `form-control-${p.size}`,
              p.state === false && 'is-invalid',
              p.state === true && 'is-valid'
            ],
            value: current(p) ?? '',
            readonly: attrs.readonly || p.plaintext,
            onInput: (e) => {
              let value = e.target.value
              if (p.trim) value = value.trim()
              if (p.number && value !== '' && !Number.isNaN(Number(value))) value = Number(value)
              if (!p.lazy) publish(emit, value)
            },
            onChange: (e) => {
              if (p.lazy) publish(emit, e.target.value)
              emit('change', e.target.value)
            }
          })
        )
    }
  })
}
export const BFormInput = control('input')
export const BFormTextarea = control('textarea')
export const BForm = defineComponent({
  inheritAttrs: false,
  props: { validated: Boolean, inline: Boolean },
  setup(p, { attrs, slots, expose }) {
    const form = ref()
    // BootstrapVue's functional BForm gave callers a native form ref. Preserve
    // the DOM APIs used by existing quote validation through our Vue 3 wrapper.
    expose({
      get elements() { return form.value?.elements },
      checkValidity: () => form.value.checkValidity(),
      reportValidity: () => form.value.reportValidity(),
      reset: () => form.value.reset(),
      submit: () => form.value.submit(),
      requestSubmit: (submitter) => form.value.requestSubmit(submitter)
    })
    return () => h('form', mergeProps(attrs, {
      ref: form,
      class: [p.validated && 'was-validated', p.inline && 'form-inline']
    }), slots.default?.())
  }
})
export const BFormGroup = defineComponent({
  inheritAttrs: false,
  props: {
    label: String,
    labelFor: String,
    labelCols: [String, Number],
    labelColsSm: [String, Number],
    labelColsMd: [String, Number],
    labelColsLg: [String, Number],
    labelColsXl: [String, Number],
    labelSize: String,
    labelClass: String,
    labelAlign: String,
    labelAlignSm: String,
    labelAlignMd: String,
    labelAlignLg: String,
    labelAlignXl: String,
    description: String,
    invalidFeedback: String,
    state: { default: null },
    item: Object,
    field: String
  },
  setup(p, { attrs, slots }) {
    const root = ref()
    const id = useId()
    const associate = () => {
      const control = root.value?.querySelector('input,select,textarea')
      const label = root.value?.querySelector('label')
      if (control && label && !p.labelFor) {
        if (!control.id) control.id = `${id}-input`
        label.htmlFor = control.id
      }
    }
    onMounted(associate)
    onUpdated(associate)
    return () => {
      const horizontal = [
        p.labelCols,
        p.labelColsSm,
        p.labelColsMd,
        p.labelColsLg,
        p.labelColsXl
      ].some(Boolean)
      const classes = (content = false) =>
        ['', 'Sm', 'Md', 'Lg', 'Xl'].flatMap((bp) => {
          const value = p[`labelCols${bp}`]
          if (!value) return []
          const prefix = bp ? `${bp.toLowerCase()}-` : ''
          return [`col-${prefix}${content ? 12 - Number(value) : value}`]
        })
      return h(
        'div',
        {
          ...Object.fromEntries(
            Object.entries(attrs).filter(
              ([key]) =>
                ['id', 'role', 'style', 'tabindex'].includes(key) ||
                /^(on[A-Z]|aria-|data-)/.test(key)
            )
          ),
          ref: root,
          class: ['form-group', horizontal && 'form-row', attrs.class]
        },
        [
          (horizontal || p.label || slots.label) &&
            h(
              'label',
              {
                for: p.labelFor,
                class: [
                  horizontal && 'col-form-label',
                  p.labelClass,
                  ...classes(),
                  p.labelSize && `col-form-label-${p.labelSize}`,
                  p.labelAlign && `text-${p.labelAlign}`,
                  ...['Sm', 'Md', 'Lg', 'Xl']
                    .filter((bp) => p[`labelAlign${bp}`])
                    .map((bp) => `text-${bp.toLowerCase()}-${p[`labelAlign${bp}`]}`)
                ]
              },
              slotContent(slots.label, p.label)
            ),
          h('div', { class: horizontal ? [!p.labelCols && 'col-12', ...classes(true)] : undefined }, [
            slots.default?.(),
            p.description && h('small', { class: 'form-text text-muted' }, p.description),
            p.state === false &&
              p.invalidFeedback &&
              h('div', { class: 'invalid-feedback d-block' }, p.invalidFeedback)
          ])
        ]
      )
    }
  }
})
export const BFormSelect = defineComponent({
  inheritAttrs: false,
  props: {
    ...inputProps,
    options: { default: () => [] },
    valueField: { default: 'value' },
    textField: { default: 'text' },
    disabledField: { default: 'disabled' },
    multiple: Boolean,
    selectSize: Number
  },
  emits: events,
  setup(p, { attrs, slots, emit }) {
    const sync = ({ el }) => {
      const value = current(p)
      el.selectedIndex = -1
      for (const option of el.options) {
        const optionValue = '_value' in option ? option._value : option.value
        option.selected = p.multiple
          ? (value || []).some((v) => equal(v, optionValue))
          : equal(value, optionValue) ||
            (value != null && typeof value !== 'object' && String(value) === option.value)
      }
    }
    const options = (entries) =>
      entries.map((o) =>
        typeof o === 'object' && o !== null
          ? o.options
            ? h('optgroup', { label: o.label }, options(o.options))
            : h(
                'option',
                {
                  value: o[p.valueField],
                  disabled: o[p.disabledField],
                  selected: Array.isArray(current(p))
                    ? current(p).includes(o[p.valueField])
                    : current(p) === o[p.valueField]
                },
                String(o[p.textField] ?? '')
              )
          : h('option', { value: o, selected: current(p) === o }, String(o))
      )
    return () =>
      h(
        'select',
        mergeProps(attrs, {
          class: [
            'custom-select',
            p.size && `custom-select-${p.size}`,
            p.state === false && 'is-invalid',
            p.state === true && 'is-valid'
          ],
          multiple: p.multiple,
          size: p.selectSize,
          onVnodeMounted: sync,
          onVnodeUpdated: sync,
          onChange: (e) => {
            const values = [...e.target.selectedOptions].map((o) =>
              '_value' in o ? o._value : o.value
            )
            const value = p.multiple ? values : values[0]
            publish(emit, value)
            emit('change', value)
          }
        }),
        [slots.first?.(), options(p.options), slots.default?.()]
      )
  }
})
export const BFormSelectOption = element('option', '')
const selectionGroup = Symbol('selectionGroup')
function selection(type) {
  return defineComponent({
    inheritAttrs: false,
    props: {
      modelValue: { default: undefined },
      checked: { default: undefined },
      value: { default: true },
      uncheckedValue: { default: false },
      switch: Boolean,
      inline: Boolean,
      disabled: Boolean,
      name: String,
      id: String
    },
    emits: events,
    setup(p, { attrs, slots, emit }) {
      const group = inject(selectionGroup, null)
      const id = p.id || useId()
      return () => {
        const selected = group
          ? current(group.props)
          : p.modelValue === undefined
          ? p.checked
          : p.modelValue
        const checked = Array.isArray(selected)
          ? selected.some((v) => equal(v, p.value))
          : equal(selected, p.value)
        const input = h(
          'input',
          mergeProps(attrs, {
            id,
            type,
            class: 'custom-control-input',
            role: p.switch ? 'switch' : undefined,
            disabled: p.disabled || group?.props.disabled,
            name: p.name || group?.name,
            required: attrs.required || group?.props.required,
            checked,
            value: p.value,
            onChange: (e) => {
              let value = type === 'radio' ? p.value : e.target.checked ? p.value : p.uncheckedValue
              if (Array.isArray(selected))
                value = e.target.checked
                  ? [...selected.filter((v) => !equal(v, p.value)), p.value]
                  : selected.filter((v) => !equal(v, p.value))
              if (group) group.update(value)
              publish(emit, value)
              emit('change', value)
            }
          })
        )
        return h(
          'div',
          {
            class: [
              'custom-control',
              p.switch ? 'custom-switch' : `custom-${type}`,
              (p.inline || (group && !group.props.stacked)) && 'custom-control-inline'
            ]
          },
          [input, h('label', { for: id, class: 'custom-control-label' }, slots.default?.())]
        )
      }
    }
  })
}
export const BFormCheckbox = selection('checkbox')
export const BFormRadio = selection('radio')
function group(Component) {
  return defineComponent({
    props: {
      ...model,
      options: { default: () => [] },
      name: String,
      disabled: Boolean,
      required: Boolean,
      stacked: Boolean,
      inline: { default: true },
      valueField: { default: 'value' },
      textField: { default: 'text' }
    },
    emits: events,
    setup(p, { emit, slots }) {
      const update = (value) => {
        publish(emit, value)
        emit('change', value)
      }
      provide(selectionGroup, { props: p, name: p.name || useId(), update })
      return () =>
        h('div', { role: Component === BFormRadio ? 'radiogroup' : 'group' }, [
          p.options.map((o) =>
            h(
              Component,
              {
                value: typeof o === 'object' ? o[p.valueField] : o,
                disabled: o?.disabled,
                inline: !p.stacked
              },
              () => String(typeof o === 'object' ? o[p.textField] : o)
            )
          ),
          slots.default?.()
        ])
    }
  })
}
export const BFormCheckboxGroup = group(BFormCheckbox)
export const BFormRadioGroup = group(BFormRadio)
export const BFormFile = defineComponent({
  inheritAttrs: false,
  props: {
    ...model,
    id: String,
    multiple: Boolean,
    disabled: Boolean,
    required: Boolean,
    state: { default: null },
    placeholder: { default: 'No file chosen' },
    dropPlaceholder: { default: 'Drop files here' },
    browseText: { default: 'Browse' },
    plain: Boolean,
    size: String
  },
  emits: events,
  setup(p, { attrs, emit, expose }) {
    const input = ref(),
      files = ref([]),
      dragging = ref(false)
    const id = p.id || useId()
    watch(
      () => current(p),
      (value) => {
        if (!value || (Array.isArray(value) && !value.length)) {
          files.value = []
          if (input.value) input.value.value = ''
        }
      }
    )
    const update = async (e, list) => {
      emit('change', e)
      files.value = Array.from(list || [])
      await nextTick()
      publish(emit, p.multiple ? files.value : files.value[0] || null)
    }
    expose({
      focus: () => input.value?.focus(),
      reset: () => {
        files.value = []
        input.value.value = ''
        publish(emit, p.multiple ? [] : null)
      }
    })
    return () => {
      const control = h(
        'input',
        mergeProps(attrs, {
          id,
          ref: input,
          type: 'file',
          disabled: p.disabled,
          required: p.required,
          multiple: p.multiple,
          class: [
            p.plain ? 'form-control-file' : 'custom-file-input',
            p.state === false && 'is-invalid',
            p.state === true && 'is-valid'
          ],
          onChange: (e) => update(e, e.target.files)
        })
      )
      if (p.plain) return control
      return h(
        'div',
        {
          class: ['custom-file', 'b-form-file', p.size && `b-form-file-${p.size}`],
          onDragover: (e) => {
            e.preventDefault()
            if (!p.disabled) dragging.value = true
          },
          onDragleave: () => {
            dragging.value = false
          },
          onDrop: (e) => {
            e.preventDefault()
            dragging.value = false
            if (p.disabled) return
            const accepts = String(attrs.accept || '')
              .toLowerCase()
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
            const list = Array.from(e.dataTransfer.files)
              .filter(
                (file) =>
                  !accepts.length ||
                  accepts.some((rule) =>
                    rule.startsWith('.')
                      ? file.name.toLowerCase().endsWith(rule)
                      : rule.endsWith('/*')
                      ? file.type.startsWith(rule.slice(0, -1))
                      : file.type === rule
                  )
              )
              .slice(0, p.multiple ? undefined : 1)
            if (!list.length) {
              emit('change', e)
              return
            }
            const transfer = new DataTransfer()
            list.forEach((file) => transfer.items.add(file))
            input.value.files = transfer.files
            update(e, list)
          }
        },
        [
          control,
          h(
            'label',
            { for: id, class: 'custom-file-label', 'data-browse': p.browseText },
            h(
              'span',
              { class: 'd-block form-file-text', style: { pointerEvents: 'none' } },
              dragging.value
                ? p.dropPlaceholder
                : files.value.length
                ? files.value.map((file) => file.name).join(', ')
                : p.placeholder
            )
          )
        ]
      )
    }
  }
})
export const BFormRow = element('div', 'form-row')
export const BFormText = element('small', 'form-text text-muted')
export const BFormInvalidFeedback = element(
  'div',
  'invalid-feedback',
  { state: { default: null } },
  (p) => ({ class: p.state === false && 'd-block' })
)
